<script setup lang="ts">
import { computed } from 'vue'

import type { RowLayout } from '../../service/layout/editLayout'

import { useI18n } from '@/shared/i18n'
import { Icon } from '@/shared/ui'

defineOptions({ name: 'AddBlockCard' })

const props = withDefaults(
  defineProps<{
    /** The shapes this grid level may create; one shape makes the whole card its button. */
    layouts?: readonly RowLayout[]
    compact?: boolean
    /** The grid is empty: v3's tall `isFirst` card (`CustomDashboardCreateCard.vue:4`). */
    first?: boolean
    /** A section is being dragged: the card turns into this grid's end-of-list drop target. */
    dropping?: boolean
    dropActive?: boolean
  }>(),
  {
    layouts: () => ['col', 'classic', 'grid'],
    compact: false,
    first: false,
    dropping: false,
    dropActive: false,
  },
)
const emit = defineEmits<{ create: [layout: RowLayout] }>()
const { t } = useI18n()

/**
 * The three shapes v3's picker offered (`CustomDashboardCreateCard.vue:14-31`), with the widths
 * `addSection` actually wrote (`CustomDashboardSection.vue:499-511`). `proportions` drives the
 * preview swatches, so the button always shows the layout it produces.
 *
 * v3 hid the picker behind `:focus` on the parent button and fell back to `col` on a plain click,
 * so two of the three shapes were reachable only by accident. They are peers here.
 */
const PROPORTIONS: Readonly<Record<RowLayout, readonly number[]>> = {
  col: [100],
  classic: [65, 35],
  grid: [50, 50],
}
const single = computed(() => props.layouts.length === 1)
</script>

<template>
  <section
    class="add-block-card group/card relative flex flex-wrap items-center justify-center rounded-lg border transition-colors"
    :class="[
      first
        ? 'min-h-72 flex-col gap-md'
        : compact
          ? 'min-h-14 gap-sm p-xs'
          : 'min-h-20 gap-md p-sm',
      dropActive
        ? 'border-dashed border-accent bg-accent-soft'
        : dropping
          ? 'border-dashed border-border-strong bg-surface'
          : 'border-border bg-surface hover:border-primary-subtle',
    ]"
    data-testid="dashboard-add-section"
  >
    <span
      v-if="dropping"
      class="add-block-card__drop inline-flex items-center gap-xs text-sm font-semibold"
      :class="dropActive ? 'text-accent' : 'text-muted'"
      data-testid="dashboard-row-drop-zone"
    >
      <Icon name="arrow-down" :size="14" />
      {{ t('dashboards.grid.dropSection') }}
    </span>
    <button
      v-else-if="single"
      type="button"
      class="add-block-card__single absolute inset-0 inline-flex cursor-pointer items-center justify-center gap-xs rounded-lg border-0 bg-transparent font-sans text-base font-semibold text-muted transition-colors hover:text-primary"
      :data-testid="`dashboard-add-section-${layouts[0]}`"
      :aria-label="t(`dashboards.grid.layout.${layouts[0]}`)"
      @click="emit('create', layouts[0]!)"
    >
      <Icon name="plus" :size="16" />
      {{ t('dashboards.grid.newSection') }}
    </button>
    <template v-else>
      <span
        class="add-block-card__label inline-flex items-center gap-xs font-semibold text-muted transition-colors group-hover/card:text-primary"
        :class="first ? 'flex-col text-2xl' : 'text-base'"
      >
        <Icon :name="first ? 'circle-plus' : 'plus'" :size="first ? 28 : 16" />
        {{ t('dashboards.grid.newSection') }}
      </span>
      <span class="add-block-card__options inline-flex gap-xs">
        <button
          v-for="layout in layouts"
          :key="layout"
          type="button"
          class="add-block-card__option group/option flex cursor-pointer gap-3xs rounded-sm border-2 border-primary-subtle bg-surface p-2xs transition-colors hover:border-primary focus-visible:border-primary focus-visible:outline-none"
          :class="first ? 'h-16 w-28' : 'h-9 w-16'"
          :data-testid="`dashboard-add-section-${layout}`"
          :aria-label="t(`dashboards.grid.layout.${layout}`)"
          :title="t(`dashboards.grid.layout.${layout}`)"
          @click="emit('create', layout)"
        >
          <span
            v-for="(share, index) in PROPORTIONS[layout]"
            :key="index"
            class="add-block-card__swatch rounded-xs bg-primary-subtle transition-colors group-hover/option:bg-info-soft group-focus-visible/option:bg-info-soft"
            :style="{ flexBasis: `${share}%` }"
          />
        </button>
      </span>
    </template>
  </section>
</template>
